import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const isPngDataUrl = (value) => typeof value === 'string' && value.startsWith('data:image/png;base64,');

const extractBase64 = (dataUrl) => {
  if (!isPngDataUrl(dataUrl)) return null;
  const [, base64] = dataUrl.split(',');
  return base64 || null;
};

const toCellValue = (value) => {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'string') return value;
  return String(value);
};

const exportExcel = async (...args) => {
  const params =
    typeof args[0] === 'string'
      ? {
          title: args[0],
          filename: args[1],
          sheetsMap: args[2],
          theme: args[3],
          t: args[4],
          branding: args[5] || {},
        }
      : args[0] || {};

  const {
    title,
    filename,
    sheetsMap,
    theme,
    branding = {},
  } = params;

  if (!sheetsMap || !(sheetsMap instanceof Map) || sheetsMap.size === 0) {
    console.error('exportExcel: sheetsMap inválido ou vazio');
    return;
  }

  const workbook = new ExcelJS.Workbook();
  const headerStyleColor = (theme?.palette?.primary?.main || '#1976d2').replace('#', '').toUpperCase();

  let imageId = null;
  const base64Logo = extractBase64(branding.header?.logoUrl);
  if (base64Logo) {
    try {
      imageId = workbook.addImage({ base64: branding.header.logoUrl, extension: 'png' });
    } catch (error) {
      console.warn('exportExcel: não foi possível inserir logomarca', error);
    }
  }

  sheetsMap.forEach((rows, sheetTitle) => {
    const worksheet = workbook.addWorksheet(sheetTitle);
    const headers = Object.keys(rows[0] || {});

    let currentRowIndex = 1;

    if (branding.header?.title) {
      const titleRow = worksheet.addRow([branding.header.title]);
      if (headers.length > 1) {
        worksheet.mergeCells(currentRowIndex, 1, currentRowIndex, headers.length);
      }
      titleRow.font = { size: 16, bold: true };
      titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
      currentRowIndex += 1;
    }

    if (branding.header?.subtitle) {
      const subtitleRow = worksheet.addRow([branding.header.subtitle]);
      if (headers.length > 1) {
        worksheet.mergeCells(currentRowIndex, 1, currentRowIndex, headers.length);
      }
      subtitleRow.font = { size: 12 };
      subtitleRow.alignment = { horizontal: 'center', vertical: 'middle' };
      currentRowIndex += 1;
    }

    if (branding.header?.generatedOn) {
      const generatedRow = worksheet.addRow([branding.header.generatedOn]);
      if (headers.length > 1) {
        worksheet.mergeCells(currentRowIndex, 1, currentRowIndex, headers.length);
      }
      generatedRow.font = { size: 10, italic: true };
      generatedRow.alignment = { horizontal: 'center', vertical: 'middle' };
      currentRowIndex += 1;
    }

    worksheet.addRow([]);
    currentRowIndex += 1;

    const headerRow = worksheet.addRow(headers.map((header) => toCellValue(header)));
    headerRow.eachCell((cell) => {
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: `FF${headerStyleColor}` },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    rows.forEach((rowData) => {
      const rowValues = headers.map((key) => toCellValue(rowData[key]));
      const row = worksheet.addRow(rowValues);
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
      rowValues.forEach((value, index) => {
        const column = worksheet.getColumn(index + 1);
        const textLength = String(value ?? '').length;
        column.width = Math.max(column.width || 12, textLength + 6);
      });
    });

    headers.forEach((header, index) => {
      const column = worksheet.getColumn(index + 1);
      column.width = Math.max(column.width || 12, header.length + 6);
    });

    if (imageId !== null) {
      try {
        worksheet.addImage(imageId, {
          tl: { col: 0, row: 0 },
          ext: { width: 200, height: 60 },
        });
      } catch (error) {
        console.warn('exportExcel: falha ao ancorar logomarca', error);
      }
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const safeFilename = filename?.endsWith('.xlsx') ? filename : `${filename || title}.xlsx`;
  saveAs(blob, safeFilename);
};

export default exportExcel;
